import { useEffect } from "react";
import { toast } from "react-toastify";

import { useApi } from "../../../../Utils/useApi";

const versionRegex = /(\d*)\.(\d*)\.(.*)/;

class ApplicationVersion {
  majorVersion: number;
  minorVersion: number;
  patchVersion: string;

  constructor(versionString: string) {
    const versionTokens = versionRegex.exec(versionString);
    if (!versionTokens || versionTokens.length != 4) throw "Failed to parse version number.";

    this.majorVersion = Number(versionTokens[1]);
    this.minorVersion = Number(versionTokens[2]);
    this.patchVersion = versionTokens[3];
  }

  /** Returns true if:
   * 1. the version's major or minor version numbers are not identical.
   * 2. The first params patch version is newer than the second param's.
   * Otherwise returns false.
   */
  static shouldAnnounceNewerVersion = (target: ApplicationVersion, source: ApplicationVersion): boolean => {
    if (target.majorVersion !== source.majorVersion) return true;
    if (target.minorVersion !== source.minorVersion) return true;
    return target.patchVersion > source.patchVersion;
  };
}

const useAnnounceNewerVersion = () => {
  const { response } = useApi<string>({ url: "Legacy/V0/Version" });

  useEffect(() => {
    try {
      if (
        response &&
        process.env.REACT_APP_VERSION_NO &&
        ApplicationVersion.shouldAnnounceNewerVersion(
          new ApplicationVersion(response),
          new ApplicationVersion(process.env.REACT_APP_VERSION_NO)
        )
      ) {
        toast.info(
          `Version ${response} is available. Please get the latest version by logging out and using Shift+F5 to refresh.`,
          { autoClose: false }
        );
      }
    } catch {
      console.error(
        `Failed to determine if server version '${response}' is newer than local version '${process.env.REACT_APP_VERSION_NO}'.`
      );
    }
  }, [response]);
};

export default useAnnounceNewerVersion;
